import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { useTranslation } from "react-i18next";
import styles from "./CognitiveReport.module.css";
import { jsPDF } from 'jspdf'; 
import html2canvas from 'html2canvas';

interface TestScore {
  testType: string;
  score: number;
  threshold: number;
  totalCount: number;
  isRisk: boolean;
}

interface RiskFactor {
  keyword: string;
  description: string;
}

interface SharedResult {
  kind: string;
  createdAt?: any;
  testSetId: string;
  totalRiskCount: number;
  riskFactors: RiskFactor[];
  testScores: TestScore[];
}

const CognitiveReport: React.FC = () => {
  const { id, hospitalId } = useParams<{ id: string; hospitalId?: string }>();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const { t } = useTranslation();

  const reportContentRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

  const navigate = useNavigate();
  const [data, setData] = useState<SharedResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalBodyOverflow = document.body.style.overflow;
    const root = document.getElementById('root');
    const originalRootOverflow = root ? root.style.overflow : '';

    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    if (root) root.style.overflow = 'auto';

    return () => {
      document.documentElement.style.overflow = originalHtmlOverflow;
      document.body.style.overflow = originalBodyOverflow;
      if (root) root.style.overflow = originalRootOverflow;
    };
  }, []);

  useEffect(() => {
    const fetchResult = async () => {
      if (!id) {
        setError("잘못된 접근입니다.");
        setLoading(false);
        return;
      }
      try {
        const docRef = doc(db, "sharedCognitiveResults", id);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setData(snap.data() as SharedResult);
        } else {
          setError("결과를 찾을 수 없습니다.");
        }
      } catch (err) {
        console.error("Firestore getDoc error:", err);
        setError("결과 불러오기에 실패했습니다.");
      } finally {
        setLoading(false);
      }
    };
    fetchResult();
  }, [id]);

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString("ko-KR", {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(/\. /g, '.').slice(0, -1); 
  };

  const handleDownloadPdf = async () => {
    const container = reportContentRef.current;
    if (!container) {
        alert("오류: 리포트 화면을 찾을 수 없습니다.");
        return;
    }

    setIsGeneratingPdf(true);
    const originalWidth = container.style.width;
    
    try {
      container.style.width = '720px'; 
      window.scrollTo(0, 0);
      await new Promise((resolve) => setTimeout(resolve, 500));

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pdfWidth - (margin * 2);
      const contentHeight = pdfHeight - (margin * 2); 

      const elements = Array.from(container.querySelectorAll('[data-pdf-section="true"]'));

      let currentHeight = 0; 
      let currentPage = 1; 

      for (let i = 0; i < elements.length; i++) {
        const el = elements[i] as HTMLElement;
        
        const canvas = await html2canvas(el, {
          scale: 2,
          useCORS: true,
          backgroundColor: '#ffffff',
          logging: false,
        } as any);

        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (currentHeight + imgHeight > contentHeight) {
          pdf.addPage(); 
          currentPage++;
          currentHeight = 0;
        }

        pdf.addImage(imgData, 'JPEG', margin, margin + currentHeight, contentWidth, imgHeight);
        currentHeight += imgHeight + 2;
      }
      
      const footer = container.querySelector('[data-pdf-footer="true"]') as HTMLElement;
      if (footer) {
         const footerCanvas = await html2canvas(footer, { scale: 2, backgroundColor: '#ffffff' } as any);
         const footerImg = footerCanvas.toDataURL('image/jpeg', 0.95);
         const footerHeight = (footerCanvas.height * contentWidth) / footerCanvas.width;

         if (currentHeight + footerHeight > contentHeight) {
            pdf.addPage();
            currentPage++; 
            currentHeight = 0;
         }
         pdf.addImage(footerImg, 'JPEG', margin, margin + currentHeight, contentWidth, footerHeight);
      }

      const totalPages = pdf.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(10);
        pdf.setTextColor(150);
        pdf.text(`${i} / ${totalPages}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      pdf.save('cognitive-report.pdf');

    } catch (error: any) {
      console.error("PDF Error:", error);
      alert(`저장 실패: ${error.message || error}`);
    } finally {
      setIsGeneratingPdf(false);
      if (container) {
        container.style.width = originalWidth; 
      }
    }
  };

  // --- 헬퍼: 테스트별 정보 (설명서) ---
  const getTestInfo = (testType: string) => {
    // DB에 저장된 testType이 "test1"일 수도 있고 "digit_span"일 수도 있으므로 매핑
    let typeKey = testType;

    // 혹시라도 옛날 키값(digit_span 등)이 들어오면 test1 형식으로 변환 (호환성 유지)
    if (testType === 'digit_span') typeKey = 'test1';
    if (testType === 'random_cube') typeKey = 'test2';
    if (testType === 'verbal_learning_test') typeKey = 'test3';
    if (testType === 'tower_of_london') typeKey = 'test4';

    switch (typeKey) {
      case "test1":
        return {
          title: t("test_type_test1", "숫자 순서 맞추기"),
          domain: t("domain_test1", "주의력 및 작업 기억"),
          desc: t("desc_test1", "청각적 정보를 단기적으로 기억하고 조작하는 능력을 평가합니다.")
        };
      case "test2":
        return {
          title: t("test_type_test2", "입체 도형 맞추기"),
          domain: t("domain_test2", "시공간 작업 기억"),
          desc: t("desc_test2", "시각적 정보의 위치와 깊이를 파악하는 능력을 평가합니다.")
        };
      case "test3":
        return {
          title: t("test_type_test3", "단어 기억 테스트"),
          domain: t("domain_test3", "언어적 기억력"),
          desc: t("desc_test3", "새로운 언어 정보를 학습하고 기억해내는 능력을 평가합니다.")
        };
      case "test4":
        return {
          title: t("test_type_test4", "하노이의 탑"),
          domain: t("domain_test4", "실행 기능 및 계획력"),
          desc: t("desc_test4", "목표 달성을 위해 순서를 계획하고 문제를 해결하는 전두엽 기능을 평가합니다.")
        };
      default:
        // 매핑되지 않는 키가 오면 그대로 출력하되, 도메인은 빈 문자열로 두어 회색 박스 방지
        return { 
          title: testType, 
          domain: "", // 빈 문자열이면 렌더링 시 조건부 처리 가능
          desc: t("desc_default", "검사 설명 정보를 불러올 수 없습니다.") 
        };
    }
  };

  if (loading) return <div className={styles.loading}>⏳ {t("loading") || "결과를 불러오는 중..."}</div>;
  if (error) return (
      <div className={styles.error}>
        <p>{error}</p>
        <button onClick={() => navigate(`${prefix}/`)}>{t("back_to_home") || "홈으로"}</button>
      </div>
    );
  if (!data) return null;

  return (
    <div className={styles.reportWrapper}>
      <div className={styles.reportContainer} ref={reportContentRef}>
        
        {/* Header (StrokeReport와 동일 디자인) */}
        <header className={styles.header} data-pdf-section="true">
          <div className={styles.logo}>
            <span style={{ backgroundColor: "#F9A825" }}></span>
            <span style={{ backgroundColor: "#1E88E5" }}></span>
            <span style={{ backgroundColor: "#E53935" }}></span>
            <span style={{ backgroundColor: "#43A047" }}></span>
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.mainTitle}>BrainCheck</h1>
            <h2 className={styles.subTitle}>{t("cognitive_report_title", "인지 기능 검사 결과")}</h2>
            <div className={styles.metaInfo}>
              <span>{`검사 일자 ${formatDate(data.createdAt)}`}</span>
              <span>v1.0.0</span>
              <span>{`UID ${id?.substring(0, 12) || 'N/A'}`}</span>
            </div>
          </div>
        </header>

        <section className={styles.contentSection}>
          
          {/* 1. 종합 요약 섹션 */}
          <div className={styles.sectionHeader} data-pdf-section="true">
             <h3 className={styles.categoryTitle}>{t("summary_title", "종합 요약")}</h3>
             <span className={`${styles.riskBadge} ${data.totalRiskCount > 0 ? styles.bgRed : styles.bgGreen}`}>
                {data.totalRiskCount > 0 
                  ? t("risk_count_msg", "{{count}}개 영역 주의 필요", { count: data.totalRiskCount })
                  : t("all_normal_msg", "모든 영역 정상 범위")}
             </span>
          </div>
          
          <p className={styles.sectionDescription} data-pdf-section="true">
            {t("cognitive_summary_desc", "4가지 인지 영역에 대한 정량적 평가 결과입니다. 각 항목의 점수와 수행 수준을 확인하세요.")}
          </p>

          {/* 2. 상세 결과 리스트 */}
          <div className={styles.testList}>
            {data.testScores.map((test, index) => {
              const info = getTestInfo(test.testType);
              const percent = Math.min((test.score / test.totalCount) * 100, 100);

              return (
                <div key={index} className={styles.testCard} data-pdf-section="true">
                  {/* 카드 헤더: 제목 및 도메인 */}
                  <div className={styles.cardHeader}>
                    <h4 className={styles.testTitle}>{info.title}</h4>
                    {/* 도메인 정보가 있을 때만 뱃지 표시 (회색 박스 방지) */}
                    {info.domain && (
                      <span className={styles.domainBadge}>{info.domain}</span>
                    )}
                  </div>

                  {/* 카드 설명 */}
                  <p className={styles.testDesc}>{info.desc}</p>

                  {/* 결과 바 (Score Bar) */}
                  <div className={styles.resultContainer}>
                    <div className={styles.scoreRow}>
                        <span className={styles.scoreLabel}>{t("score", "내 점수")}</span>
                        <div className={styles.scoreValueBox}>
                           <span className={styles.currentScore}>{test.score}</span>
                           <span className={styles.totalScore}> / {test.totalCount}</span>
                        </div>
                    </div>
                    
                    {/* 프로그레스 바 */}
                    <div className={styles.progressBarBg}>
                       <div 
                         className={`${styles.progressBarFill} ${test.isRisk ? styles.fillRed : styles.fillGreen}`}
                         style={{ width: `${percent}%` }}
                       ></div>
                    </div>

                    {/* 판정 텍스트 */}
                    <div className={styles.statusRow}>
                       <span className={styles.statusLabel}>{t("result", "판정")}</span>
                       <span className={`${styles.statusText} ${test.isRisk ? styles.textRed : styles.textGreen}`}>
                          {test.isRisk ? t("risk", "주의 필요 (Risk)") : t("normal", "정상 (Normal)")}
                       </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 3. 위험 요인 설명 (있을 경우에만) */}
          {data.riskFactors && data.riskFactors.length > 0 && (
            <>
              <div style={{ height: '40px' }} data-pdf-section="true"></div>
              <div className={styles.sectionHeader} data-pdf-section="true">
                <h3 className={styles.categoryTitle}>{t("risk_factor_analysis", "식별된 위험 요인 분석")}</h3>
              </div>
              <div className={styles.riskList}>
                {data.riskFactors.map((rf, idx) => (
                  <div key={idx} className={styles.riskItem} data-pdf-section="true">
                     <span className={styles.riskKeyword}>{rf.keyword}</span>
                     <span className={styles.riskDesc}>{rf.description}</span>
                  </div>
                ))}
              </div>
            </>
          )}

        </section>

        <div className={styles.footerInfo} data-pdf-footer="true">
             BrainCheck © 2025 SHMD. All rights reserved.
        </div>
      </div>

      <div style={{ textAlign: 'center', padding: '20px' }}>
        <button 
          onClick={handleDownloadPdf} 
          className={styles.printButton}
          disabled={isGeneratingPdf}
          style={{ position: 'relative', zIndex: 9999, cursor: 'pointer' }} 
        >
          {isGeneratingPdf ? "PDF 생성 중..." : "📄 PDF로 저장 / 인쇄"}
        </button>
      </div>

    </div>
  );
};

export default CognitiveReport;